import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import classNames from "classnames";
import lottie, { AnimationItem } from "lottie-web";

import { useMediaQuery } from "@hooks/use-media-query";
import { calcAnimateDelay } from "@helpers/helpers";
import { ICharacterData } from "@typings/global.types";

type PropsType = {
  character: ICharacterData;
  width?: number;
  height?: number;
};

export type CharacterHandle = {
  bite: () => void;
  jump: () => void;
  error: () => void;
};

export const CharacterLottieWeb = forwardRef<CharacterHandle, PropsType>(
  ({ character, width = 100, height = 100 }, ref) => {
    const isMdScreen = useMediaQuery({ minWidth: "md" });
    const lottieRef = useRef<AnimationItem>();

    const lottieDivRef = useRef<HTMLDivElement>(null);
    const inactionTimerRef = useRef<
      ReturnType<typeof setInterval> | undefined
    >();
    const animationTimeoutRef = useRef<
      ReturnType<typeof setTimeout> | undefined
    >();

    useImperativeHandle(ref, () => ({
      bite() {
        runAnimateByType("bite");
      },
      jump() {
        runAnimateByType("jump");
      },
      error() {
        runAnimateByType("error");
      },
    }));

    const runInactionAnimate = useCallback(
      (playerInstance: AnimationItem) => {
        playerInstance.setLoop(true);
        playerInstance.setSegment(
          character.timing.inaction[0],
          character.timing.inaction[1],
        );
        playerInstance.play();
      },
      [character.timing.inaction],
    );
    const runAnimateByType = useCallback(
      (type: keyof ICharacterData["timing"]) => {
        const playerInstance = lottieRef.current;

        if (playerInstance) {
          if (type === "inaction") {
            runInactionAnimate(playerInstance);
          } else {
            playerInstance.setLoop(false);
            const animationTiming = character.timing[type];

            if (inactionTimerRef.current) {
              clearInterval(inactionTimerRef.current);
            }

            playerInstance.playSegments(animationTiming, true);

            if (animationTimeoutRef.current) {
              clearTimeout(animationTimeoutRef.current);
            }

            animationTimeoutRef.current = setTimeout(() => {
              runInactionAnimate(playerInstance);
            }, calcAnimateDelay(animationTiming));
          }
        }
      },
      [character.timing, runInactionAnimate],
    );

    useEffect(() => {
      if (lottieDivRef.current) {
        lottieRef.current = lottie.loadAnimation({
          container: lottieDivRef.current,
          animationData: character.lottieFile,
          renderer: "svg",
          autoplay: false,
          loop: false,
        });
        lottieRef.current.addEventListener("DOMLoaded", () => {
          lottieRef.current?.setSubframe(false);
          runAnimateByType("inaction");
        });
      }

      return () => {
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
        lottie.destroy();
      };
    }, [character.lottieFile, runAnimateByType, width]);

    // w-[${width}px] h-[${height}px] - мають бути додані в safelist tailwind
    const characterClassNames = `w-[${width}px] h-[${height}px]`;

    return <div ref={lottieDivRef} className={`flex ${characterClassNames}`} />;
  },
);
